import { IConfigFunctionArgs } from 'Components/Worksheets/Core';
import { getSieveSizeFromId } from 'Components/Worksheets/WashWorksheet/helpers';
import { get, joinPaths } from 'Lib/Object/Path/shared';
import { convertLabelMapToConfig, convertLabelUomMapToConfig, mergeConfig } from '../../../Core/Helpers/configHelpers';

const getLabelConfig = (prefix: string) =>
  mergeConfig(
    convertLabelMapToConfig({
      [`${prefix}.HydrometerPortion.Preparation.PreparationMethod`]: 'Material Preparation Method',
      [`${prefix}.HydrometerPortion.Preparation.ParticleDensityDeterminationMethod`]: 'Particle Density Determination',
      [`${prefix}.HydrometerPortion.Preparation.ParticleDensity`]: 'Particle Density',
      [`${prefix}.HydrometerPortion.Preparation.AssociatedParticleDensityTest.ExternalTest.Results.ParticleDensity`]:
        'Particle Density',
      [`${prefix}.HydrometerPortion.Preparation.GrainSize`]: 'Specimen Grain Size',
      [`${prefix}.HydrometerPortion.Preparation.ContainsOversizeParticles`]:
        'Specimen contains particles >2mm in diameter',

      //Imported Sieve Analysis results
      [`${prefix}.Preparation.Sieve_2_0`]: 'Passing 2 mm',
      [`${prefix}.Preparation.Sieve_0_063`]: 'Passing 63 µm',
      [`${prefix}.Preparation.TestStandardAndMethod`]: 'Test Method',

      [`${prefix}.HydrometerPortion.Masses.MassMeasurement.Equipment.Balance`]: 'Balance',
      [`${prefix}.HydrometerPortion.Masses.MassMeasurement.TareMass`]: 'Tare Mass',
      [`${prefix}.HydrometerPortion.Masses.MassMeasurement.TareAndWetMass`]: 'Tare and Total Material Mass',
      [`${prefix}.HydrometerPortion.Masses.WetMass`]: 'Total Material Mass',
      [`${prefix}.HydrometerPortion.Masses.DryMass`]: 'Dry Material Mass',

      [`${prefix}.HydrometerPortion.Pretreatment.UntreatedMaterialDryMass`]: 'Untreated Material Dry Mass',
      [`${prefix}.HydrometerPortion.Pretreatment.Reagent`]: 'Reagent Used',
      [`${prefix}.HydrometerPortion.Pretreatment.SeparationMethod`]: 'Separation of Soil and Reagent by',
      [`${prefix}.HydrometerPortion.Pretreatment.Equipment.Balance`]: 'Pre-Treated Material Balance',
      [`${prefix}.HydrometerPortion.Pretreatment.TareID`]: 'Tare ID',
      [`${prefix}.HydrometerPortion.Pretreatment.TareMass`]: 'Tare Mass',
      [`${prefix}.HydrometerPortion.Pretreatment.TareAndPretreatedMaterialDryMass`]:
        'Tare and Pre-Treated Material Dry Mass',
      [`${prefix}.HydrometerPortion.Pretreatment.PretreatedMaterialDryMass`]: 'Pre-Treated Material Dry Mass',

      [`${prefix}.HydrometerPortion.Agitation.DispersingAgent`]: 'Dispersing Agent',
      [`${prefix}.HydrometerPortion.Agitation.DateDispersingAgentPrepared`]: 'Date Dispersing Agent Prepared',
      [`${prefix}.HydrometerPortion.Agitation.DispersingAgentVolume`]: 'Volume of Dispersing Agent Used',
      [`${prefix}.HydrometerPortion.Agitation.DateTimeAgitationStartedLocal`]: 'Agitation Started',
      [`${prefix}.HydrometerPortion.Agitation.DateTimeAgitationFinishedLocal`]: 'Agitation Finished',
      [`${prefix}.HydrometerPortion.Agitation.Duration`]: 'Duration of Agitation',

      [`${prefix}.HydrometerPortion.Measurements.Equipment.SedimentationCylinder`]: 'Sedimentation Cylinder',
      [`${prefix}.HydrometerPortion.Measurements.Equipment.Hydrometer`]: 'Hydrometer',
      [`${prefix}.HydrometerPortion.Measurements.HydrometerCalibrationMeniscusCorrection`]: 'Meniscus Correction',
      [`${prefix}.HydrometerPortion.Measurements.HydrometerCalibrationIntercept`]: 'Intercept',
      [`${prefix}.HydrometerPortion.Measurements.HydrometerCalibrationSlope`]: 'Slope',

      [`${prefix}.HydrometerPortion.Measurements.TimeSequence`]: 'Time Series to Use',
      [`${prefix}.HydrometerPortion.Measurements.DateTimeTestStartedLocal`]: 'Test Start Time',

      [`${prefix}.HydrometerPortion.Measurements.Readings.DateTimeReadingLocal`]: 'Date & Time',
      [`${prefix}.HydrometerPortion.Measurements.Readings.RawReading`]: "Reading, R'h",
      [`${prefix}.HydrometerPortion.Measurements.Readings.TrueReading`]: 'True Reading, Rh',
      [`${prefix}.HydrometerPortion.Measurements.Readings.ElapsedTimeInMinutes`]: 'Elapsed Time',
      [`${prefix}.HydrometerPortion.Measurements.Readings.Temperature`]: 'Temperature',
      [`${prefix}.HydrometerPortion.Measurements.Readings.EffectiveDepth`]: 'Effective Depth, Hr',
      [`${prefix}.HydrometerPortion.Measurements.Readings.ModifiedReading`]: 'Modified Reading, Rd',
      [`${prefix}.HydrometerPortion.Measurements.Readings.ParticleDiameter`]: 'Particle Diameter',
      [`${prefix}.HydrometerPortion.Measurements.Readings.MassSmallerPercentUnscaled`]: 'Mass Smaller, K',
      [`${prefix}.HydrometerPortion.Measurements.Readings.MassSmallerPercentScaled`]: 'Scaled to Whole, Kc',
      [`${prefix}.HydrometerPortion.Measurements.Readings.Exclude`]: 'Exclude',

      [`${prefix}.HydrometerPortion.Measurements.ReferenceReadings.RawReading`]: "Reading, R'0",
      [`${prefix}.HydrometerPortion.Measurements.ReferenceReadings.Temperature`]: 'Temperature',

      [`${prefix}.HydrometerPortion.Washing.AssociatedWashTest.MeasuredTest.Preparation.IsEvaporationRequired`]:
        'Evaporation Required',
      [`${prefix}.HydrometerPortion.Washing.AssociatedWashTest.MeasuredTest.Masses.WashedDryMass`]: 'Dry Mass Retained',
      [`${prefix}.HydrometerPortion.Washing.AssociatedWashTest.MeasuredTest.Masses.WashingsDryMass`]:
        'Dry Mass Passing',
      [`${prefix}.HydrometerPortion.Washing.AssociatedWashTest.MeasuredTest.Results.PercentRetainedWashingSieve`]: (
        args: IConfigFunctionArgs
      ) => {
        const { WashingSieveSizeId: washingSieveSizeId, AllSieveSizes: allSieveSizes } =
          get(
            args.worksheet,
            joinPaths(prefix, 'HydrometerPortion.Washing.AssociatedWashTest.MeasuredTest.Equipment')
          ) ?? {};

        return `Dry Mass Retained on ${
          getSieveSizeFromId(washingSieveSizeId, allSieveSizes)?.Caption ?? '63 µm'
        } Sieve`;
      },
      [`${prefix}.HydrometerPortion.Washing.AssociatedWashTest.MeasuredTest.Results.PercentPassingWashingSieve`]: (
        args: IConfigFunctionArgs
      ) => {
        const { WashingSieveSizeId: washingSieveSizeId, AllSieveSizes: allSieveSizes } =
          get(
            args.worksheet,
            joinPaths(prefix, 'HydrometerPortion.Washing.AssociatedWashTest.MeasuredTest.Equipment')
          ) ?? {};

        return `Dry Mass Passing ${getSieveSizeFromId(washingSieveSizeId, allSieveSizes)?.Caption ?? '63 µm'} Sieve`;
      },
      [`${prefix}.Results.ChartOptions.CurveFittingAlgorithm`]: 'Curve Fitting',
      [`${prefix}.Results.ChartOptions.JoinSpecificationPoints`]: 'Join specification points on chart',
      [`${prefix}.Results.D85`]: 'D85',
      [`${prefix}.Results.D60`]: 'D60',
      [`${prefix}.Results.D50`]: 'D50',
      [`${prefix}.Results.D30`]: 'D30',
      [`${prefix}.Results.D15`]: 'D15',
      [`${prefix}.Results.D10`]: 'D10',

      [`${prefix}.Results.ReportPSDCurveAnalysis`]: 'D60, D30, D10',
    }),
    convertLabelUomMapToConfig({
      [`${prefix}.HydrometerPortion.Measurements.Readings.RawReading`]: 'g/mL',
      [`${prefix}.HydrometerPortion.Measurements.Readings.TrueReading`]: 'g/mL',
      [`${prefix}.HydrometerPortion.Measurements.Readings.ElapsedTimeInMinutes`]: 'min',
      [`${prefix}.HydrometerPortion.Measurements.Readings.ModifiedReading`]: 'g/mL',
      [`${prefix}.HydrometerPortion.Measurements.Readings.MassSmallerPercentUnscaled`]: '%',
      [`${prefix}.HydrometerPortion.Measurements.Readings.MassSmallerPercentScaled`]: '%',

      [`${prefix}.HydrometerPortion.Measurements.ReferenceReadings.RawReading`]: 'g/mL',
      [`${prefix}.HydrometerPortion.Measurements.ReferenceReadings.TrueReading`]: 'g/mL',

      [`${prefix}.HydrometerPortion.Measurements.HydrometerCalibrationMeniscusCorrection`]: 'g/mL',
    })
  );

export default getLabelConfig;
